import { equal, like } from '@/api/helpers';
import { FiltersType, ListingRequest, ListingResponse } from '@/api/types';
import { ITEMS_PER_LAZY_REQUEST } from '@/utils/helpers';
import { debounce } from 'lodash';
import {
  Dropdown,
  DropdownChangeEvent,
  DropdownProps,
} from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Skeleton } from 'primereact/skeleton';
import {
  VirtualScroller,
  VirtualScrollerLazyEvent,
} from 'primereact/virtualscroller';
import React, {
  HTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type LazyLoadingSelectProps = {
  fetchService?: (
    params: ListingRequest & { search?: string },
  ) => Promise<ListingResponse<any>>;
  handleOnChange: (value: any) => void;
  selectedValue: any;
  selectedValueFilterBy?: string;
  additionalFilters?: FiltersType;
  additionalOption?: any;
  optionFilter?: (value: string) => FiltersType;
  useSearch?: boolean;
  filterPlaceholder?: string;
  refetchKey?: number;
  isDisabled?: boolean;
  label?: string;
  dataKey?: string;
} & DropdownProps &
  Omit<HTMLAttributes<HTMLSelectElement>, 'onChange' | 'defaultValue'>;

export const LazyLoadingSelect: React.FC<LazyLoadingSelectProps> = ({
  fetchService,
  handleOnChange,
  selectedValue,
  selectedValueFilterBy = 'id',
  additionalFilters,
  additionalOption,
  optionFilter = (value) => [like('name', value)],
  useSearch = false,
  filterPlaceholder,
  refetchKey = 0,
  isDisabled,
  dataKey,
  ...rest
}) => {
  const ref = useRef<Dropdown>(null);
  const searchValueRef = useRef<string>('');
  const skipRef = useRef(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<any>([]);
  const [visibleData, setVisibleData] = useState<any>([]);
  const [fetchedSelectedValue, setFetchedSelectedValue] = useState<any>();
  const [skip, setSkip] = useState(0);
  const [count, setCount] = useState(0);
  const [filter, setFilter] = useState<FiltersType>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [filterValue, setFilterValue] = useState<string>('');
  const selectedValueRef = useRef<any>(selectedValue);
  const pendingSelectedValueKeyRef = useRef<any>();

  searchValueRef.current = searchValue;
  skipRef.current = skip;
  selectedValueRef.current = selectedValue;

  const handleFetchItems = async (
    filter?: FiltersType,
    isFilterOrRefetch?: boolean,
    search?: string,
  ) => {
    setIsLoading(true);

    if (isFilterOrRefetch) {
      setSkip(0);
    }

    const request = fetchService
      ? await fetchService({
          filters: [...(additionalFilters ?? []), ...(filter ?? [])],
          skip: isFilterOrRefetch ? 0 : skipRef.current,
          take: ITEMS_PER_LAZY_REQUEST,
          ...(useSearch && search !== undefined ? { search } : {}),
        })
      : undefined;

    const fetchedData = request?.result ?? [];

    if (isFilterOrRefetch) {
      setData([]);
      setVisibleData([]);
    }

    setCount(request?.count ?? 0);
    setData((prevData: any) => [
      ...(additionalOption &&
      !prevData.find((item: any) =>
        selectedValueFilterBy
          ? item?.[selectedValueFilterBy] ===
            additionalOption?.[selectedValueFilterBy]
          : item.id === additionalOption.id,
      )
        ? [additionalOption]
        : []),
      ...prevData,
      ...fetchedData,
    ]);
    setVisibleData((prevData: any) => [
      ...(additionalOption &&
      !prevData.find((item: any) =>
        selectedValueFilterBy
          ? item?.[selectedValueFilterBy] ===
            additionalOption?.[selectedValueFilterBy]
          : item.id === additionalOption.id,
      )
        ? [additionalOption]
        : []),
      ...prevData,
      ...fetchedData,
    ]);

    setIsLoading(false);
  };

  const handleFetchSelectedValue = async () => {
    if (!selectedValue) return;
    if (!fetchService) return;

    if (
      selectedValueFilterBy
        ? selectedValue?.[selectedValueFilterBy] ===
          additionalOption?.[selectedValueFilterBy]
        : selectedValue.id === additionalOption.id
    ) {
      setFetchedSelectedValue(additionalOption);
      return;
    }

    const existingItem = data.find((item: any) =>
      selectedValueFilterBy
        ? item?.[selectedValueFilterBy] ===
          selectedValue?.[selectedValueFilterBy]
        : item.id === selectedValue.id,
    );

    if (existingItem) {
      setFetchedSelectedValue(existingItem);
      return;
    }

    const requestedKey = selectedValue[selectedValueFilterBy];

    if (pendingSelectedValueKeyRef.current === requestedKey) return;

    pendingSelectedValueKeyRef.current = requestedKey;

    try {
      const request = await fetchService({
        filters: [
          ...(additionalFilters ?? []),
          equal(selectedValueFilterBy, requestedKey),
        ],
        skip: 0,
        take: 1,
      });
      const result = request?.result?.pop();

      if (
        result &&
        selectedValueRef.current?.[selectedValueFilterBy] === requestedKey
      ) {
        setFetchedSelectedValue(result);
      }
    } catch {
      return;
    } finally {
      if (pendingSelectedValueKeyRef.current === requestedKey) {
        pendingSelectedValueKeyRef.current = undefined;
      }
    }
  };

  useEffect(() => {
    if (!refetchKey) {
      return;
    }

    const handleRefetch = async () => {
      await handleFetchItems(undefined, true);
    };

    handleRefetch();
  }, [refetchKey]);

  useEffect(() => {
    handleFetchItems();
    setSkip(skip + ITEMS_PER_LAZY_REQUEST);
  }, []);

  useEffect(() => {
    if (selectedValue) {
      handleFetchSelectedValue();
    } else {
      setVisibleData(data);
      setFetchedSelectedValue(null);
    }
  }, [selectedValue, data]);

  useEffect(() => {
    if (filter.length > 0 || (selectedValue && data.length > 0)) {
      clearSelectedValueDuplicates();
    } else {
      setVisibleData(data);
    }
  }, [data, fetchedSelectedValue, selectedValue, filter]);

  const onLazyLoad = (event: VirtualScrollerLazyEvent) => {
    if (!isLoading && event.last >= data.length && skipRef.current <= count) {
      const nextSkip = skipRef.current + ITEMS_PER_LAZY_REQUEST;
      setSkip(nextSkip);
      handleFetchItems(
        filter,
        false,
        useSearch ? searchValueRef.current : undefined,
      );
    }
  };

  const onFilter = useMemo(
    () =>
      debounce((event) => {
        const value = event?.target?.value || '';
        setSkip(0);
        skipRef.current = 0;

        if (useSearch) {
          setSearchValue(value);
          searchValueRef.current = value;
          setFilter([]);
          handleFetchItems(undefined, true, value);
        } else {
          setFilter(optionFilter(value));
          handleFetchItems(optionFilter(value), true);
        }
        setSkip(ITEMS_PER_LAZY_REQUEST);
        skipRef.current = ITEMS_PER_LAZY_REQUEST;

        if (data?.length > ITEMS_PER_LAZY_REQUEST && ref.current) {
          const vs: VirtualScroller | null = ref.current.getVirtualScroller();
          vs?.scrollInView(1, 'to-end');
        }
      }, 500),
    [optionFilter, data.length, useSearch],
  );

  const clearSelectedValueDuplicates = () => {
    if (selectedValue) {
      setVisibleData(
        data.filter(
          (value: any) =>
            value[selectedValueFilterBy] !==
            selectedValue[selectedValueFilterBy],
        ),
      );
    }
  };

  const loadingTemplate = () => (
    <div className="flex align-items-center p-2">
      <Skeleton height="32px" />
    </div>
  );

  const filterTemplate = (
    <div className="p-dropdown-filter-container w-full">
      <span className="p-input-icon-right w-full">
        <i className="pi pi-search" />
        <InputText
          className="p-dropdown-filter p-inputtext p-component"
          placeholder={filterPlaceholder || rest.placeholder}
          autoFocus
          defaultValue={filterValue}
          onChange={onFilter}
          onBlur={(e) => setFilterValue(e.target.value)}
        />
      </span>
    </div>
  );

  const virtualScrollerOptions = () => {
    if (visibleData?.length > 8) {
      return {
        itemSize: 50,
        lazy: true,
        onScrollIndexChange: onLazyLoad,
        onLazyLoad: () => clearSelectedValueDuplicates(),
        loadingTemplate,
        showLoader: true,
        loading: isLoading,
        step: ITEMS_PER_LAZY_REQUEST,
        autoSize: true,
      };
    }

    return undefined;
  };

  const displayedSelectedValue =
    !selectedValue ||
    fetchedSelectedValue?.[selectedValueFilterBy] ===
      selectedValue[selectedValueFilterBy]
      ? fetchedSelectedValue
      : rest.optionLabel && selectedValue[rest.optionLabel] !== undefined
      ? selectedValue
      : null;

  return (
    <Dropdown
      ref={ref}
      dataKey={dataKey || 'id'}
      value={!isDisabled ? displayedSelectedValue : selectedValue}
      dropdownIcon="pi pi-chevron-down"
      options={
        !isDisabled
          ? [displayedSelectedValue, ...visibleData].filter(Boolean)
          : selectedValue
          ? [selectedValue]
          : []
      }
      disabled={isDisabled}
      virtualScrollerOptions={virtualScrollerOptions()}
      filterTemplate={filterTemplate}
      onChange={(e: DropdownChangeEvent) => {
        handleOnChange(e.value);
      }}
      {...(rest as DropdownProps)}
    />
  );
};
